import { create } from "zustand";
import {
  api,
  IInventoryResponse,
  IBrandGroup,
} from "../api/api";

// Статическая таблица размеров US для обуви
const US_SIZES = [
  "3.5",
  "4",
  "4.5",
  "5",
  "5.5",
  "6",
  "6.5",
  "7",
  "7.5",
  "8",
  "8.5",
  "9",
  "9.5",
  "10",
  "10.5",
  "11",
  "11.5",
  "12",
  "12.5",
  "13",
  "13.5",
  "14",
  "14.5",
  "15",
  "15.5",
  "16",
];

// Новый интерфейс для работы с группированными данными
export interface GroupedInventoryItem {
  article: string;
  name: string;
  model: string;
  color: string;
  imageUrl: string;
  sizes: {
    id: number;
    size: string;
    price: string;
    avgMarketPrice: string;
    maxPrice: string;
    minPrice: string;
    cost: string;
    goatPrice: string;
    stockXPrice: string;
    poizonPrice: string;
    condition?: string;
    city?: string;
    place?: string;
    fitting?: boolean;
    vendor?: {
      first_name: string;
      last_name: string;
      telegram_account: string;
      vk_account: string;
    };
    inStore: boolean;
    isSale: boolean;
    saleDate?: string | null;
    purchaseDate?: string | null;
    purchasePrice?: number;
    status?: string;
    shipmentDate?: string | null;
    inShipmentDays?: number;
    buyerName?: string;
  }[];
  priceRange: {
    min: number;
    max: number;
  };
  avgProfit: number; // Средняя прибыль в рублях
  avgProfitPercent: number; // Средняя прибыль в процентах
}

// Старый интерфейс для обратной совместимости
export interface InventoryItem {
  id: number;
  name: string;
  model: string;
  color: string;
  size: string;
  price: string;
  article: string;
  avgMarketPrice: string;
  maxPrice: string;
  minPrice: string;
  cost: string;
  goatPrice: string;
  stockXPrice: string;
  poizonPrice: string;
  imageUrl: string;
  status?: "active" | "sold" | "hidden";
  condition?: string;
  provider?: string;
  provider_name?: string;
  city?: string;
  place?: string;
  fitting?: boolean;
  vendor?: {
    first_name: string;
    last_name: string;
    telegram_account: string;
    vk_account: string;
  };
  inStore: boolean;
  isSale: boolean;
  saleDate?: string | null;
  purchaseDate?: string | null;
}

// Интерфейс состояния хранилища
interface InventoryState {
  // Данные в новом формате
  groupedItems: GroupedInventoryItem[];
  groupedSalesItems: GroupedInventoryItem[];
  groupedShipmentsItems: GroupedInventoryItem[];

  // Пагинация
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  hasMore: boolean;

  // Пагинация для продаж
  salesCurrentPage: number;
  salesTotalItems: number;
  salesItemsPerPage: number;

  // Состояния загрузки
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;

  // Ошибки
  error: string | null;

  // Флаг инициализации
  hasInitialized: boolean;

  // Поиск
  searchQuery: string;
  isSearching: boolean;
  searchResults: GroupedInventoryItem[];
  salesSearchResults: GroupedInventoryItem[];
  shipmentsSearchResults: GroupedInventoryItem[];
  searchCurrentPage: number;
  searchTotalItems: number;
  searchItemsPerPage: number;

  // Фильтры
  selectedBrandId: number | null;
  selectedModelId: string | null;
  selectedArticleId: string | null;
  selectedSize: string | null;
  priceFrom: number | null;
  priceTo: number | null;
  isSale: boolean | null;

  // Данные для фильтров (устаревшие, оставляем для совместимости)
  availableBrands: string[];

  // Состояния загрузки фильтров
  isLoadingBrands: boolean;
  isLoadingModels: boolean;
  isLoadingArticles: boolean;

  // Флаги загрузки данных фильтров
  brandsLoaded: boolean;
  modelsLoaded: boolean;
  articlesLoaded: boolean;

  // Данные для фильтров с ID
  availableBrandsWithId: { id: number; name: string }[];
  availableModels: string[];
  availableArticles: string[];
  availableSizes: string[];

  // Методы для работы с API
  fetchInventory: (
    offset?: number,
    limit?: number,
    isSale?: boolean,
    system?: string,
    brandId?: number,
    model?: string,
    article?: string,
    size?: string,
    priceFrom?: number,
    priceTo?: number
  ) => Promise<void>;
  fetchSales: (offset?: number, limit?: number) => Promise<void>;
  fetchShipments: () => Promise<void>;
  fetchItemById: (id: number) => Promise<InventoryItem>;
  createItem: (data: Partial<InventoryItem>) => Promise<void>;
  updateItem: (id: number, data: Partial<InventoryItem>) => Promise<void>;
  deleteItem: (id: number) => Promise<void>;
  deleteSneaker: (id: number) => Promise<void>;
  markAsSold: (id: number, buyer: string) => Promise<void>;
  markAsHidden: (id: number) => Promise<void>;
  markAsVisible: (id: number) => Promise<void>;
  importItems: (items: Partial<InventoryItem>[]) => Promise<void>;

  // Поиск
  searchInventory: (
    query: string,
    limit?: number,
    offset?: number
  ) => Promise<void>;
  searchSales: (
    query: string,
    limit?: number,
    offset?: number
  ) => Promise<void>;
  searchShipments: (query: string) => void;
  clearSearch: () => void;
  clearSearchResults: () => void;
  setSearchPage: (page: number, isSales?: boolean) => void;
  setSearchItemsPerPage: (count: number, isSales?: boolean) => void;

  // Управление пагинацией продаж
  setSalesPage: (page: number) => void;
  setSalesItemsPerPage: (count: number) => void;

  // Фильтры
  setSelectedBrandId: (brandId: number | null) => void;
  setSelectedModelId: (modelId: string | null) => void;
  setSelectedArticleId: (articleId: string | null) => void;
  setSelectedSize: (size: string | null) => void;
  setPriceFrom: (price: number | null) => void;
  setPriceTo: (price: number | null) => void;
  setIsSale: (isSale: boolean | null) => void;
  clearFilters: () => void;
  applyFilters: () => Promise<void>;

  // Загрузка данных для фильтров
  fetchAllBrands: () => Promise<void>;
  fetchAllModels: () => Promise<void>;
  fetchAllArticles: () => Promise<void>;

  // Вспомогательный метод для получения текущих параметров фильтрации
  getCurrentFilterParams: () => {
    selectedBrandId: number | null;
    selectedModelId: string | null;
    selectedArticleId: string | null;
    selectedSize: string | null;
    priceFrom: number | null;
    priceTo: number | null;
    isSale: boolean | null;
  };

  // Пагинация
  setPage: (page: number) => void;
  setItemsPerPage: (count: number) => void;

  // Сброс состояния
  resetInventoryState: () => void;
}

// Функция для преобразования данных API в группированный формат
const transformApiDataToGroupedItems = (
  brandGroups: IBrandGroup[]
): GroupedInventoryItem[] => {
  const groupedItems: GroupedInventoryItem[] = [];

  // Проверяем, что brandGroups существует и не пустой
  if (!brandGroups || brandGroups.length === 0) {
    return groupedItems;
  }

  brandGroups.forEach((brandGroup) => {
    // Проверяем, что products существует и не пустой
    if (!brandGroup.products || brandGroup.products.length === 0) {
      return;
    }

    brandGroup.products.forEach((product) => {
      // Проверяем, что sneakers существует и не пустой
      if (!product.sneakers || product.sneakers.length === 0) {
        return;
      }

      const sizes = product.sneakers.map((sneakerWithPhoto) => {
        const { sneaker, prices } = sneakerWithPhoto;
        return {
          id: sneaker.id,
          size: `${sneaker.us_size} US`,
          price: `${sneaker.price} ₽`,
          avgMarketPrice: `${prices.avg_price} ₽`,
          maxPrice: `${Math.max(prices.goat, prices.poizon, prices.stock_x)} ₽`,
          minPrice: (() => {
            const validPrices = [
              prices.goat,
              prices.poizon,
              prices.stock_x,
            ].filter((p) => p > 0);
            return validPrices.length > 0
              ? `${Math.min(...validPrices)} ₽`
              : "0 ₽";
          })(),
          cost: `${sneaker.purchase_price} ₽`,
          goatPrice: `${prices.goat} ₽`,
          stockXPrice: `${prices.stock_x} ₽`,
          poizonPrice: `${prices.poizon} ₽`,
          condition: sneaker.condition.condition,
          city: sneaker.city,
          place: sneaker.place,
          fitting: sneaker.fitting,
          vendor: {
            first_name: sneaker.vendor.first_name,
            last_name: sneaker.vendor.last_name,
            telegram_account: sneaker.vendor.telegram_account || "",
            vk_account: sneaker.vendor.vk_account || "",
          },
          inStore: sneaker.in_store,
          isSale: sneaker.is_sale,
          saleDate: sneaker.sale_date,
          purchaseDate: sneaker.purchase_date,
          buyerName: sneaker.buyer_name || undefined,
        };
      });

      const firstSneaker = product.sneakers[0]?.sneaker;
      if (firstSneaker) {
        const prices = sizes.map((s) =>
          parseFloat(s.price.replace(/[^\d]/g, ""))
        );
        
        // Расчет средней прибыли
        const costs = sizes.map((s) =>
          parseFloat(s.cost.replace(/[^\d]/g, ""))
        );
        
        // Средняя цена продажи и себестоимость
        const avgPrice = prices.length > 0 
          ? prices.reduce((sum, price) => sum + price, 0) / prices.length 
          : 0;
        const avgCost = costs.length > 0 
          ? costs.reduce((sum, cost) => sum + cost, 0) / costs.length 
          : 0;
        
        // Средняя прибыль в рублях
        const avgProfit = avgPrice - avgCost;
        
        // Средняя прибыль в процентах
        const avgProfitPercent = avgCost > 0 
          ? (avgProfit / avgCost) * 100 
          : 0;
        
        // Удаляем бренд из модели, если он там есть
        const brand = firstSneaker.brand.brand;
        let modelWithoutBrand = firstSneaker.model;
        if (modelWithoutBrand.startsWith(brand)) {
          modelWithoutBrand = modelWithoutBrand.substring(brand.length).trim();
        }
        
        groupedItems.push({
          article: product.article,
          name: brand,
          model: modelWithoutBrand,
          color: firstSneaker.color,
          imageUrl:
            product.sneakers[0]?.photo_url || "https://placehold.co/80x80",
          sizes,
          priceRange: {
            min: prices.length > 0 ? Math.min(...prices) : 0,
            max: prices.length > 0 ? Math.max(...prices) : 0,
          },
          avgProfit: Math.round(avgProfit),
          avgProfitPercent: Math.round(avgProfitPercent * 100) / 100,
        });
      }
    });
  });

  return groupedItems;
};

// Начальное состояние
const initialState = {
  groupedItems: [],
  groupedSalesItems: [],
  groupedShipmentsItems: [],
  currentPage: 0,
  totalItems: 0,
  itemsPerPage: 5,
  hasMore: true,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  hasInitialized: false,
  searchQuery: "",
  isSearching: false,
  searchResults: [],
  salesSearchResults: [],
  shipmentsSearchResults: [],
  searchCurrentPage: 0,
  searchTotalItems: 0,
  searchItemsPerPage: 5,

  // Пагинация для продаж
  salesCurrentPage: 0,
  salesTotalItems: 0,
  salesItemsPerPage: 5,

  // Фильтры
  selectedBrandId: null,
  selectedModelId: null,
  selectedArticleId: null,
  selectedSize: null,
  priceFrom: null,
  priceTo: null,
  isSale: null,

  // Данные для фильтров (устаревшие, оставляем для совместимости)
  availableBrands: [],

  // Состояния загрузки фильтров
  isLoadingBrands: false,
  isLoadingModels: false,
  isLoadingArticles: false,

  // Флаги загрузки данных фильтров
  brandsLoaded: false,
  modelsLoaded: false,
  articlesLoaded: false,

  // Данные для фильтров с ID
  availableBrandsWithId: [],
  availableModels: [],
  availableArticles: [],
  availableSizes: US_SIZES,
};

// Создание хранилища
export const useInventoryStore = create<InventoryState>()((set, get) => ({
  ...initialState,

  // Получение списка товаров инвентаря с пагинацией
  fetchInventory: async (
    offset = 0,
    limit = 5,
    isSale?: boolean,
    system = "inventory",
    brandId?: number,
    model?: string,
    article?: string,
    size?: string,
    priceFrom?: number,
    priceTo?: number
  ) => {
    set({ isLoading: true, error: null });

    try {
      const response: IInventoryResponse = await api.getSneakers(
        offset,
        limit,
        isSale,
        system,
        brandId,
        model,
        article,
        size,
        priceFrom,
        priceTo
      );

      const groupedItems = transformApiDataToGroupedItems(response.data || []);
      const totalInventoryItems = response.total_inventory_count || 0;
      const nextOffset = offset + limit;
      const hasMoreData = nextOffset < totalInventoryItems;

      set({
        groupedItems,
        totalItems: totalInventoryItems,
        hasMore: hasMoreData,
        isLoading: false,
        hasInitialized: true,
      });

      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при загрузке инвентаря:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Неизвестная ошибка при загрузке инвентаря",
        isLoading: false,
        hasInitialized: true,
      });
      return Promise.reject(error);
    }
  },

  // Получение списка проданных товаров
  fetchSales: async (offset = 0, limit = 5) => {
    set({ isLoading: true, error: null });

    try {
      const {
        selectedBrandId,
        selectedModelId,
        selectedArticleId,
        selectedSize,
        priceFrom,
        priceTo,
      } = get();

      const response: IInventoryResponse = await api.getSneakers(
        offset,
        limit,
        true,
        "inventory",
        selectedBrandId || undefined,
        selectedModelId || undefined,
        selectedArticleId || undefined,
        selectedSize || undefined,
        priceFrom || undefined,
        priceTo || undefined
      );
      const groupedSalesItems = transformApiDataToGroupedItems(response.data);

      set({
        groupedSalesItems,
        salesTotalItems: response.total_inventory_count || 0,
        isLoading: false,
      });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при загрузке продаж:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Неизвестная ошибка при загрузке продаж",
        isLoading: false,
      });
      return Promise.reject(error);
    }
  },

  // Получение списка отгрузок
  fetchShipments: async () => {
    set({ isLoading: true, error: null });

    try {
      const response: IInventoryResponse = await api.getShipments(0, 100);
      const groupedShipmentsItems = transformApiDataToGroupedItems(
        response.data
      );

      set({ groupedShipmentsItems, isLoading: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при загрузке отгрузок:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Неизвестная ошибка при загрузке отгрузок",
        isLoading: false,
      });
      return Promise.reject(error);
    }
  },

  // Получение конкретного товара по ID
  fetchItemById: async (id: number): Promise<InventoryItem> => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.getSneakerById(id);
      const { sneaker, photo_url, prices } = response;

      // Преобразуем данные из API в формат InventoryItem
      const item: InventoryItem = {
        id: sneaker.id,
        name: sneaker.brand.brand,
        model: sneaker.model,
        color: sneaker.color,
        size: `${sneaker.us_size} US`,
        price: `${sneaker.price} ₽`,
        article: sneaker.article,
        avgMarketPrice: `${prices.avg_price} ₽`,
        maxPrice: `${Math.max(prices.goat, prices.poizon, prices.stock_x)} ₽`,
        minPrice: `${Math.min(
          ...[prices.goat, prices.poizon, prices.stock_x].filter((p) => p > 0)
        )} ₽`,
        cost: `${sneaker.purchase_price} ₽`,
        goatPrice: `${prices.goat} ₽`,
        stockXPrice: `${prices.stock_x} ₽`,
        poizonPrice: `${prices.poizon} ₽`,
        imageUrl: photo_url || "",
        condition: sneaker.condition.condition,
        provider_name: sneaker.provider_name || "",
        city: sneaker.city,
        place: sneaker.place,
        fitting: sneaker.fitting,
        vendor: {
          first_name: sneaker.vendor.first_name,
          last_name: sneaker.vendor.last_name,
          telegram_account: sneaker.vendor.telegram_account || "",
          vk_account: sneaker.vendor.vk_account || "",
        },
        inStore: sneaker.in_store,
        isSale: sneaker.is_sale,
        saleDate: sneaker.sale_date,
        purchaseDate: sneaker.purchase_date,
      };

      set({ isLoading: false });
      return Promise.resolve(item);
    } catch (error) {
      console.error("Ошибка при загрузке товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при загрузке товара",
        isLoading: false,
      });
      return Promise.reject(error);
    }
  },

  // Создание нового товара
  createItem: async (data: any) => {
    set({ isCreating: true, error: null });

    try {
      const apiData = {
        uk_size: data.uk_size,
        us_size: data.us_size,
        eu_size: data.eu_size,
        brand: data.brand,
        price: data.price,
        article: data.article,
        condition: data.condition,
        provider: data.provider,
        city: data.city,
        place: data.place,
        fitting: data.fitting,
        purchase_date: data.purchase_date,
        purchase_price: data.purchase_price,
      };

      await api.createSneakersLot(apiData);

      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      set({ isCreating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при создании товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при создании товара",
        isCreating: false,
      });
      return Promise.reject(error);
    }
  },

  updateItem: async (id: number, data: Partial<InventoryItem>) => {
    set({ isUpdating: true, error: null });

    try {
      const usSize = data.size ? parseFloat(data.size.replace(" US", "")) : 0;

      const apiData = {
        id,
        uk_size: usSize + 0.5,
        us_size: usSize,
        eu_size: usSize + 33,
        brand: data.name || "",
        price: data.price ? parseFloat(data.price.replace(/[^\d]/g, "")) : 0,
        article: data.article || "",
        condition: data.condition || "",
        provider: data.provider || "",
        city: data.city || "",
        place: data.place || "",
        fitting: data.fitting || false,
        purchase_price: data.cost
          ? parseFloat(data.cost.replace(/[^\d]/g, ""))
          : 0,
        purchase_date: data.purchaseDate || "",
      };

      await api.updateSneaker(apiData);

      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      set({ isUpdating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при обновлении товара:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Ошибка при обновлении товара",
        isUpdating: false,
      });
      return Promise.reject(error);
    }
  },

  deleteItem: async (id: number) => {
    set({ isDeleting: true, error: null });

    try {
      await api.deleteSneaker(id);

      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      set({ isDeleting: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при удалении товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при удалении товара",
        isDeleting: false,
      });
      return Promise.reject(error);
    }
  },

  // Удаление кроссовки (для продаж)
  deleteSneaker: async (id: number) => {
    set({ isDeleting: true, error: null });

    try {
      await api.deleteSneaker(id);

      // Обновляем данные продаж после удаления
      await get().fetchSales();

      set({ isDeleting: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при удалении товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при удалении товара",
        isDeleting: false,
      });
      return Promise.reject(error);
    }
  },

  // Отметить как проданный
  markAsSold: async (id: number, buyer: string) => {
    set({ isUpdating: true, error: null });

    try {
      await api.setSoldOut({ id, buyer });

      // Сохраняем текущую страницу при обновлении
      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      // Обновляем данные продаж
      await get().fetchSales();

      set({ isUpdating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при отметке товара как проданного:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Ошибка при отметке товара как проданного",
        isUpdating: false,
      });
      return Promise.reject(error);
    }
  },

  // Скрыть товар
  markAsHidden: async (id: number) => {
    set({ isUpdating: true, error: null });

    try {
      await api.deleteFromStore(id);

      // Сохраняем текущую страницу при обновлении
      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      set({ isUpdating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при скрытии товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при скрытии товара",
        isUpdating: false,
      });
      return Promise.reject(error);
    }
  },

  // Показать товар
  markAsVisible: async (id: number) => {
    set({ isUpdating: true, error: null });

    try {
      await api.putInStore(id);

      // Сохраняем текущую страницу при обновлении
      const { currentPage, itemsPerPage } = get();
      const filterParams = get().getCurrentFilterParams();
      await get().fetchInventory(
        currentPage * itemsPerPage,
        itemsPerPage,
        filterParams.isSale || undefined,
        "inventory",
        filterParams.selectedBrandId || undefined,
        filterParams.selectedModelId || undefined,
        filterParams.selectedArticleId || undefined,
        filterParams.selectedSize || undefined,
        filterParams.priceFrom || undefined,
        filterParams.priceTo || undefined
      );

      set({ isUpdating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при показе товара:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при показе товара",
        isUpdating: false,
      });
      return Promise.reject(error);
    }
  },

  // Импорт товаров
  importItems: async (items: Partial<InventoryItem>[]) => {
    set({ isCreating: true, error: null });

    try {
      for (const item of items) {
        await get().createItem(item);
      }

      set({ isCreating: false });
      return Promise.resolve();
    } catch (error) {
      console.error("Ошибка при импорте товаров:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при импорте товаров",
        isCreating: false,
      });
      return Promise.reject(error);
    }
  },

  // Поиск в инвентаре
  searchInventory: async (query: string, limit = 5, offset = 0) => {
    if (!query.trim()) {
      set({
        searchResults: [],
        searchQuery: "",
        isSearching: false,
        searchCurrentPage: 0,
        searchTotalItems: 0,
      });
      return;
    }

    set({ isSearching: true, searchQuery: query, error: null });

    try {
      const response = await api.searchSneakers(query, false, limit, offset);

      // API возвращает данные в стандартном формате с группировкой
      const transformedData = transformApiDataToGroupedItems(
        response.data || []
      );

      set({
        searchResults: transformedData,
        searchTotalItems: response.total_inventory_count || 0,
        isSearching: false,
      });
    } catch (error) {
      console.error("Ошибка при поиске товаров:", error);
      set({
        error:
          error instanceof Error ? error.message : "Ошибка при поиске товаров",
        isSearching: false,
        searchResults: [],
        searchTotalItems: 0,
      });
    }
  },

  // Поиск в продажах
  searchSales: async (query: string, limit = 5, offset = 0) => {
    if (!query.trim()) {
      set({
        salesSearchResults: [],
        searchQuery: "",
        isSearching: false,
        searchCurrentPage: 0,
        searchTotalItems: 0,
      });
      return;
    }

    set({ isSearching: true, searchQuery: query, error: null });

    try {
      const response = await api.searchSneakers(query, true, limit, offset);

      // API возвращает данные в стандартном формате с группировкой
      const transformedData = transformApiDataToGroupedItems(
        response.data || []
      );

      set({
        salesSearchResults: transformedData,
        searchTotalItems: response.total_inventory_count || 0,
        isSearching: false,
      });
    } catch (error) {
      console.error("Ошибка при поиске проданных товаров:", error);
      set({
        error:
          error instanceof Error
            ? error.message
            : "Ошибка при поиске проданных товаров",
        isSearching: false,
        salesSearchResults: [],
        searchTotalItems: 0,
      });
    }
  },

  // Локальный поиск в отгрузках
  searchShipments: (query: string) => {
    const { groupedShipmentsItems } = get();

    if (!query.trim()) {
      set({
        shipmentsSearchResults: [],
        searchQuery: "",
        isSearching: false,
        searchCurrentPage: 0,
        searchTotalItems: 0,
      });
      return;
    }

    set({ isSearching: true, searchQuery: query, error: null });

    // Локальный поиск по модели, цвету, артикулу и размерам
    const searchQueryLower = query.toLowerCase().trim();
    const filteredItems = groupedShipmentsItems.filter((item) => {
      // Поиск по модели
      if (item.model.toLowerCase().includes(searchQueryLower)) return true;

      // Поиск по цвету
      if (item.color.toLowerCase().includes(searchQueryLower)) return true;

      // Поиск по артикулу
      if (item.article.toLowerCase().includes(searchQueryLower)) return true;

      // Поиск по размерам
      if (
        item.sizes.some((size) =>
          size.size.toLowerCase().includes(searchQueryLower)
        )
      )
        return true;

      return false;
    });

    set({
      shipmentsSearchResults: filteredItems,
      searchTotalItems: filteredItems.length,
      isSearching: false,
    });
  },

  // Очистка поиска
  clearSearch: () => {
    set({
      searchQuery: "",
      searchResults: [],
      salesSearchResults: [],
      shipmentsSearchResults: [],
      isSearching: false,
      searchCurrentPage: 0,
      searchTotalItems: 0,
    });
  },

  // Очистка только результатов поиска (без searchQuery)
  clearSearchResults: () => {
    set({
      searchResults: [],
      salesSearchResults: [],
      isSearching: false,
      searchCurrentPage: 0,
      searchTotalItems: 0,
    });
  },

  // Установка текущей страницы
  setPage: (page: number) => {
    set({ currentPage: page, isLoading: true });
    const { itemsPerPage } = get();
    const filterParams = get().getCurrentFilterParams();
    get().fetchInventory(
      page * itemsPerPage,
      itemsPerPage,
      filterParams.isSale || undefined,
      "inventory",
      filterParams.selectedBrandId || undefined,
      filterParams.selectedModelId || undefined,
      filterParams.selectedArticleId || undefined,
      filterParams.selectedSize || undefined,
      filterParams.priceFrom || undefined,
      filterParams.priceTo || undefined
    );
  },

  // Установка количества элементов на странице
  setItemsPerPage: (count: number) => {
    set({ itemsPerPage: count, currentPage: 0, isLoading: true });
    const filterParams = get().getCurrentFilterParams();
    get().fetchInventory(
      0,
      count,
      filterParams.isSale || undefined,
      "inventory",
      filterParams.selectedBrandId || undefined,
      filterParams.selectedModelId || undefined,
      filterParams.selectedArticleId || undefined,
      filterParams.selectedSize || undefined,
      filterParams.priceFrom || undefined,
      filterParams.priceTo || undefined
    );
  },

  // Установка текущей страницы поиска
  setSearchPage: (page: number, isSales = false) => {
    set({ searchCurrentPage: page });
    const { searchItemsPerPage, searchQuery } = get();
    if (isSales) {
      get().searchSales(
        searchQuery,
        searchItemsPerPage,
        page * searchItemsPerPage
      );
    } else {
      get().searchInventory(
        searchQuery,
        searchItemsPerPage,
        page * searchItemsPerPage
      );
    }
  },

  // Установка количества элементов на странице поиска
  setSearchItemsPerPage: (count: number, isSales = false) => {
    set({ searchItemsPerPage: count, searchCurrentPage: 0 });
    const { searchQuery } = get();
    if (isSales) {
      get().searchSales(searchQuery, count, 0);
    } else {
      get().searchInventory(searchQuery, count, 0);
    }
  },

  // Управление пагинацией продаж
  setSalesPage: (page: number) => {
    set({ salesCurrentPage: page });
    const { salesItemsPerPage } = get();
    get().fetchSales(page * salesItemsPerPage, salesItemsPerPage);
  },

  setSalesItemsPerPage: (count: number) => {
    set({ salesItemsPerPage: count, salesCurrentPage: 0 });
    get().fetchSales(0, count);
  },

  // Фильтры
  setSelectedBrandId: (brandId: number | null) => {
    set({ selectedBrandId: brandId, currentPage: 0 });
  },

  setSelectedModelId: (modelId: string | null) => {
    set({ selectedModelId: modelId, currentPage: 0 });
  },

  setSelectedArticleId: (articleId: string | null) => {
    set({ selectedArticleId: articleId, currentPage: 0 });
  },

  setSelectedSize: (size: string | null) => {
    set({ selectedSize: size, currentPage: 0 });
  },

  setPriceFrom: (price: number | null) => {
    set({ priceFrom: price, currentPage: 0 });
  },

  setPriceTo: (price: number | null) => {
    set({ priceTo: price, currentPage: 0 });
  },

  setIsSale: (isSale: boolean | null) => {
    set({ isSale: isSale, currentPage: 0 });
  },

  clearFilters: () => {
    set({
      selectedBrandId: null,
      selectedModelId: null,
      selectedArticleId: null,
      selectedSize: null,
      priceFrom: null,
      priceTo: null,
      isSale: null,
      currentPage: 0,
    });
  },

  applyFilters: async () => {
    const {
      selectedBrandId,
      selectedModelId,
      selectedArticleId,
      selectedSize,
      priceFrom,
      priceTo,
      isSale,
      itemsPerPage,
      currentPage,
    } = get();
    await get().fetchInventory(
      currentPage * itemsPerPage,
      itemsPerPage,
      isSale || undefined,
      "inventory",
      selectedBrandId || undefined,
      selectedModelId || undefined,
      selectedArticleId || undefined,
      selectedSize || undefined,
      priceFrom || undefined,
      priceTo || undefined
    );
  },

  // Вспомогательный метод для получения текущих параметров фильтрации
  getCurrentFilterParams: () => {
    const {
      selectedBrandId,
      selectedModelId,
      selectedArticleId,
      selectedSize,
      priceFrom,
      priceTo,
      isSale,
    } = get();
    return {
      selectedBrandId,
      selectedModelId,
      selectedArticleId,
      selectedSize,
      priceFrom,
      priceTo,
      isSale,
    };
  },

  // Загрузка данных для фильтров
  fetchAllBrands: async () => {
    const { brandsLoaded } = get();
    if (brandsLoaded) return;

    set({ isLoadingBrands: true });
    try {
      const brands = await api.getUserBrands();
      const brandsWithId = brands.map((brand) => ({
        id: brand.id,
        name: brand.brand,
      }));
      set({
        availableBrands: brands.map((brand) => brand.brand),
        availableBrandsWithId: brandsWithId,
        isLoadingBrands: false,
        brandsLoaded: true,
      });
    } catch (error) {
      console.error("Ошибка при загрузке брендов:", error);
      set({
        availableBrands: [],
        availableBrandsWithId: [],
        isLoadingBrands: false,
      });
    }
  },

  fetchAllModels: async () => {
    const { modelsLoaded } = get();
    if (modelsLoaded) return;

    set({ isLoadingModels: true });
    try {
      const response = await api.getSneakerModels();
      set({
        availableModels: response.models,
        isLoadingModels: false,
        modelsLoaded: true,
      });
    } catch (error) {
      console.error("Ошибка при загрузке моделей:", error);
      set({ availableModels: [], isLoadingModels: false });
    }
  },

  fetchAllArticles: async () => {
    const { articlesLoaded } = get();
    if (articlesLoaded) return;

    set({ isLoadingArticles: true });
    try {
      const response = await api.getSneakerArticles();
      set({
        availableArticles: response.articles,
        isLoadingArticles: false,
        articlesLoaded: true,
      });
    } catch (error) {
      console.error("Ошибка при загрузке артикулов:", error);
      set({ availableArticles: [], isLoadingArticles: false });
    }
  },

  // Сбрасывает состояние инвентаря (для выхода из аккаунта)
  resetInventoryState: () => {
    set({
      ...initialState,
    });
  },
}));
